import React, { useCallback, useMemo, useState } from 'react';
import {
    getTiktokSongSuggestions,
    usePaginatedFetch,
    getTiktokAudioAssets,
    TiktokAudioAsset,
    linkTiktokAudioToSongs,
    rejectTiktokSongSuggestion,
    getSong,
    Song,
    getSongSuggestionsDecisionsMade,
} from '@round/api';
import useUrlState from '../../../../../Hooks/useUrlState';
import TiktokSongSuggestionsTable from './TiktokSongSuggestionsTable/TiktokSongSuggestionsTable';
import useAbortableEffect from '../../../../../Hooks/useAbortableEffect';
import { TiktokSongSuggestionsTableRow } from './TiktokSongSuggestions.types';
import { showNotification } from '../../../../../helpers';
import styles from './TiktokSongSuggestion.module.css';
import SearchInput from '../../../../../ui/DataEntry/SearchInput/SearchInput';
import cn from 'classnames';
import Button from '../../../../../ui/Buttons/Button/Button';
import { AudioPlayerProvider } from '../../../../AudioPlayer/AudioPlayerContext';
import uniq from 'lodash/uniq';

type UrlState = {
    page: number;
    page_size: number;
    search: string;
};

const TiktokSongSuggestions = () => {
    const [urlState, setUrlState] = useUrlState<UrlState>({
        page: 1,
        page_size: 25,
        search: '',
    });

    const page = Number(urlState.page);
    const setPage = (page: number) => setUrlState({ page });
    const pageSize = Number(urlState.page_size);
    const setPageSize = (pageSize: number | undefined) => setUrlState({ page_size: pageSize ?? 25 });
    const search = urlState.search;
    const setSearch = (search: string) => setUrlState({ search, page: 1 });

    const [tableLoading, setTableLoading] = useState(false);
    const {
        page: suggestionPage,
        getCurrentPage,
        count,
    } = usePaginatedFetch(getTiktokSongSuggestions, {
        page: page,
        page_size: pageSize,
        search,
    });

    const [assets, setAssets] = useState<TiktokAudioAsset[]>([]);
    const [songs, setSongs] = useState<Song[]>([]);
    const [suggestionsToFilter, setSuggestionsToFilter] = useState<number[]>([]);
    const [songsAccepted, setSongsAccepted] = useState<number | null>(null);
    const [songsRejected, setSongsRejected] = useState<number | null>(null);

    const rows: TiktokSongSuggestionsTableRow[] = useMemo(
        () =>
            suggestionPage
                .filter((s) => !suggestionsToFilter.includes(s.id))
                .map((s) => {
                    const asset = assets.find((a) => a.audio_id === s.audio_id);
                    return {
                        ...s,
                        image: asset?.image_thumb.cached_url || asset?.image_thumb.original_url || '',
                        audioUrl: asset?.audio_file.cached_url || asset?.audio_file.original_url || '',
                        song: songs.find((song) => song.id === s.song),
                    };
                }),
        [assets, songs, suggestionPage, suggestionsToFilter]
    );

    useAbortableEffect(
        (signal) => {
            setTableLoading(true);
            setSuggestionsToFilter([]);
            getCurrentPage({ signal });
        },
        [getCurrentPage]
    );

    useAbortableEffect(
        (signal) => {
            if (suggestionPage.length) {
                const audioIds = suggestionPage.map((s) => s.audio_id);
                const songIds = uniq(suggestionPage.map((s) => s.song));
                setSongs([]);

                Promise.all([
                    getTiktokAudioAssets(audioIds, { signal }).then(setAssets),
                    songIds.map((id) =>
                        getSong(id, { signal })
                            .then((response) => {
                                if (response.status === 200) {
                                    setSongs((songs) => songs.concat(response.data));
                                }
                            })
                            .catch(() => {})
                    ),
                ])
                    .catch(() => {})
                    .finally(() => setTableLoading(false));
            }

            if (count === 0) {
                setTableLoading(false);
            }
        },
        [suggestionPage, count]
    );

    const handleAccept = useCallback(
        async (suggestion: TiktokSongSuggestionsTableRow) => {
            if (!suggestion.song) {
                return;
            }

            try {
                await linkTiktokAudioToSongs(suggestion.audio_id, [suggestion.song.id]);
                showNotification('Audio added', 'info');
                setSuggestionsToFilter((s) => s.concat(suggestion.id));
                if (songsAccepted !== null) {
                    setSongsAccepted(songsAccepted + 1);
                }
            } catch {
                showNotification('Could not add audio', 'error');
            }
        },
        [songsAccepted]
    );

    const handleReject = useCallback(
        async (suggestion: TiktokSongSuggestionsTableRow) => {
            try {
                await rejectTiktokSongSuggestion(suggestion.id);
                showNotification('Audio rejected', 'info');
                setSuggestionsToFilter((s) => s.concat(suggestion.id));
                if (songsRejected !== null) {
                    setSongsRejected(songsRejected + 1);
                }
            } catch {
                showNotification('Could not reject audio', 'error');
            }
        },
        [songsRejected]
    );

    const handleLinkSongs = useCallback(
        async (suggestion: TiktokSongSuggestionsTableRow, songIds: number[]) => {
            if (!songIds.length) {
                return;
            }

            try {
                await linkTiktokAudioToSongs(suggestion.audio_id, songIds);
                showNotification('Songs linked', 'info');
                setSuggestionsToFilter((s) => s.concat(suggestion.id));
                if (songsAccepted !== null) {
                    setSongsAccepted(songsAccepted + 1);
                }
            } catch {
                showNotification('Could not link songs', 'error');
            }
        },
        [songsAccepted]
    );

    const fetchDecisionsMade = useCallback(async (requestInit: RequestInit) => {
        try {
            const midnight = new Date();
            midnight.setHours(0, 0, 0, 0);
            const midnightISO = midnight.toISOString();
            const decisionsMadeResponse = await getSongSuggestionsDecisionsMade(midnightISO, requestInit);
            setSongsAccepted(decisionsMadeResponse.data.accepted);
            setSongsRejected(decisionsMadeResponse.data.rejected);
        } catch {
            //noop
        }
    }, []);

    useAbortableEffect(
        (signal) => {
            fetchDecisionsMade({ signal });
        },
        [fetchDecisionsMade]
    );

    return (
        <div className={styles.container}>
            <div className={cn(styles.row, styles.spaceBetween)}>
                <div className={styles.stateBadges}>
                    <div className={styles.outstandingSuggestions}>
                        {count - suggestionsToFilter.length} outstanding
                    </div>
                    <div className={cn(styles.outstandingSuggestions, styles.accepted)}>
                        {songsAccepted ?? '-'} accepted
                    </div>
                    <div className={cn(styles.outstandingSuggestions, styles.rejected)}>
                        {songsRejected ?? '-'} rejected
                    </div>
                </div>

                <div className={cn(styles.row, styles.actions)}>
                    <SearchInput className={styles.search} value={urlState.search} onChange={setSearch} />
                    <Button
                        disabled={tableLoading}
                        type="filled"
                        className={styles.refreshTableButton}
                        onClick={() => {
                            setTableLoading(true);
                            getCurrentPage();
                        }}
                    >
                        Refresh table
                    </Button>
                </div>
            </div>
            <AudioPlayerProvider>
                <TiktokSongSuggestionsTable
                    loading={tableLoading}
                    data={rows}
                    count={count}
                    page={page}
                    setPage={setPage}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    onAcceptSuggestionClicked={handleAccept}
                    onRejectSuggestionClicked={handleReject}
                    onSongsLinked={handleLinkSongs}
                />
            </AudioPlayerProvider>
        </div>
    );
};

export default TiktokSongSuggestions;
